// A session ends after this long without activity, Mixpanel's own default.
export const SESSION_TIMEOUT_MS = 30 * 60 * 1000;

const STORAGE_KEY = 'analytics_session';

export interface AnalyticsSession {
	id: string;
	lastActivityAt: number;
	// Started while no one was signed in. Signing in starts a new session,
	// so the signed-in session's session_started carries the role and club.
	anonymous?: boolean;
}

interface ResolvedSession {
	session: AnalyticsSession;
	isNew: boolean;
}

/**
 * Continues the stored session at `now`, or starts a new one when there is
 * none, it has been idle for longer than the timeout, or it was anonymous and
 * someone has signed in since.
 */
export function resolveSession(
	stored: AnalyticsSession | null,
	now: number,
	anonymous: boolean = false
): ResolvedSession {
	const signedInSince = stored?.anonymous && !anonymous;

	if (
		stored?.id &&
		now - stored.lastActivityAt < SESSION_TIMEOUT_MS &&
		!signedInSince
	) {
		return { session: { ...stored, lastActivityAt: now }, isNew: false };
	}

	return {
		session: { id: newSessionId(now), lastActivityAt: now, anonymous },
		isNew: true,
	};
}

// Not crypto.randomUUID: older iOS web views lack it, and the id only has
// to tell one device's sessions apart.
function newSessionId(now: number): string {
	return `${now.toString(36)}-${secureRandomIdPart(8)}`;
}

function secureRandomIdPart(length: number): string {
	const bytes = new Uint8Array(length);
	crypto.getRandomValues(bytes);

	let out = '';
	for (const b of bytes) {
		out += (b % 36).toString(36);
	}

	return out;
}

// Kept in localStorage so every tab and app restart within the timeout
// shares one session. Storage can be unavailable; a session then lasts the
// page load.
export function readStoredSession(): AnalyticsSession | null {
	try {
		return JSON.parse(localStorage.getItem(STORAGE_KEY));
	} catch {
		return null;
	}
}

export function storeSession(session: AnalyticsSession): void {
	try {
		localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
	} catch {
		// see readStoredSession
	}
}

// Forgets the session, so the next activity starts a new one: a different
// person may be next on this device.
export function clearStoredSession(): void {
	try {
		localStorage.removeItem(STORAGE_KEY);
	} catch {
		// see readStoredSession
	}
}
